/**
 * One company on the Companies page (SA-1456 spec §7.2, §6.2 to §6.4): its fields, its spellings and what
 * names it, and Merge into…. A short form: a fixed width and no resize handle (the drawer rule).
 *
 * Every write carries the stamp the drawer opened on (`base.updated_at`, never a background refetch's), so a
 * save over someone else's change is refused `company_changed`; the drawer then says so and offers Reload,
 * which re-reads the company and keeps nothing typed. Save sends only the keys that changed.
 */
import { useId, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  Badge, Button, Drawer, DrawerBody, DrawerContent, DrawerFooter, DrawerHeader, DrawerTitle, Input,
} from '@/components/ui'
import { CompanyInput } from '@/components/companies/CompanyInput'
import { roleLabel } from '@/components/companies/companyText'
import {
  companiesKey, readCompany, refusalOf, useCompany, useMergeCompany, usePatchCompany, useRemoveSpelling, type CompanyPatch,
} from '@/hooks/useCompanies'
import type { Company, CompanyLink } from '@/types/companies'
import * as T from './companiesText'

/** Every role a company can hold, in the order the drawer offers them. */
const ROLES = ['customer', 'forwarder', 'broker', 'shipper', 'consignee', 'carrier', 'affiliate'] as const

type TextKey = 'name' | 'short_name' | 'legal_name' | 'country' | 'registration_id'
const TEXT_KEYS: readonly TextKey[] = ['name', 'short_name', 'legal_name', 'country', 'registration_id']
type Typed = Partial<Record<TextKey, string>> & { roles?: string[] }

const HEADING = 'text-xs font-semibold uppercase tracking-wide text-muted-foreground'
const QUIET = 'text-sm text-muted-foreground'

const sortedRoles = (roles: readonly string[]): string[] => [...new Set(roles)].sort()
const sameRoles = (a: readonly string[], b: readonly string[]): boolean =>
  sortedRoles(a).join(',') === sortedRoles(b).join(',')

/** What the form changes against `base`: a trimmed text, blank as null (the name stays as typed), roles sorted. */
function patchOf(base: Company, typed: Typed): CompanyPatch {
  const patch: CompanyPatch = {}
  for (const key of TEXT_KEYS) {
    const value = typed[key]
    if (value === undefined) continue
    let next: string | null = value.trim()
    if (key === 'country') next = next.toUpperCase()
    if (key !== 'name' && next === '') next = null
    if (next !== (base[key] ?? (key === 'name' ? '' : null))) patch[key] = next as never
  }
  if (typed.roles && !sameRoles(typed.roles, base.roles)) patch.roles = sortedRoles(typed.roles)
  return patch
}

function MergeSection({ base, names, busyElsewhere, onMerged, onConflict }: {
  base: Company
  names: ReadonlyMap<string, string>
  busyElsewhere: boolean
  onMerged: (from: string, into: string) => void
  onConflict: () => void
}) {
  const [open, setOpen] = useState(false)
  const [pick, setPick] = useState<CompanyLink>({ text: '', companyId: null })
  const [error, setError] = useState<string | null>(null)
  const merge = useMergeCompany()
  const intoId = pick.companyId
  const known = intoId ? names.get(intoId) : undefined
  const fetched = useCompany(intoId && !known ? intoId : null)
  const intoName = known ?? fetched.data?.name ?? null

  if (!open) {
    return (
      <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)}>{T.MERGE_OPEN}</Button>
    )
  }

  const confirm = () => {
    if (!intoId || !intoName) return
    setError(null)
    merge.mutate({ id: base.id, intoId, expectedUpdatedAt: base.updated_at }, {
      onSuccess: (survivor) => onMerged(base.name, survivor.name),
      onError: (err) => {
        const { code, message } = refusalOf(err)
        if (code === 'company_changed') { onConflict(); return }
        setError(message ?? T.MERGE_FAILED)
      },
    })
  }

  return (
    <div className="space-y-2">
      <CompanyInput pickOnly label={T.MERGE_LABEL} value={pick} onChange={(next) => { setError(null); setPick(next) }} />
      <p className={QUIET}>{T.MERGE_HINT}</p>
      {intoId === base.id && <p className="text-sm text-destructive">{T.MERGE_SELF}</p>}
      {intoId && intoId !== base.id && intoName && (
        <div data-testid="merge-confirm" className="space-y-2 rounded-md border border-border bg-muted/40 p-3">
          <p className="text-sm">{T.mergeConfirm(base.name, intoName)}</p>
          <div className="flex gap-2">
            <Button type="button" size="sm" variant="destructive" disabled={merge.isPending || busyElsewhere} onClick={confirm}>
              {merge.isPending ? T.MERGING : T.MERGE}
            </Button>
            <Button type="button" size="sm" variant="outline" disabled={merge.isPending}
              onClick={() => { setPick({ text: '', companyId: null }); setError(null); setOpen(false) }}>
              {T.CANCEL}
            </Button>
          </div>
        </div>
      )}
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  )
}

export function CompanyDrawer({ company, names, onClose, onMerged, onOpen }: {
  /** The company as it was when the row was opened: its stamp is the one every write sends. */
  company: Company
  /** Live companies' names by id, for the merge confirm. */
  names: ReadonlyMap<string, string>
  onClose: () => void
  onMerged: (from: string, into: string) => void
  /** Open another company in the drawer (the one this was merged into meanwhile). */
  onOpen: (company: Company) => void
}) {
  const ids = { name: useId(), short: useId(), legal: useId(), country: useId(), registration: useId() }
  const [base, setBase] = useState(company)
  const [typed, setTyped] = useState<Typed>({})
  const [saved, setSaved] = useState(false)
  const [conflict, setConflict] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [reloading, setReloading] = useState(false)
  // Reload found this company merged into another meanwhile: shown, never adopted as this drawer's base.
  const [gone, setGone] = useState<Company | null>(null)
  const [mergeKey, setMergeKey] = useState(0)
  const typedRef = useRef(typed)
  typedRef.current = typed
  const client = useQueryClient()
  const patch = usePatchCompany()
  const remove = useRemoveSpelling()

  const value = (key: TextKey): string => typed[key] ?? base[key] ?? ''
  const roles = typed.roles ?? base.roles
  const changes = patchOf(base, typed)
  const dirty = Object.keys(changes).length > 0
  const busy = patch.isPending || remove.isPending || reloading || gone !== null

  const edit = (next: Typed) => { setSaved(false); setError(null); setTyped((t) => ({ ...t, ...next })) }
  const toggleRole = (role: string) =>
    edit({ roles: roles.includes(role) ? roles.filter((r) => r !== role) : [...roles, role] })

  const refused = (err: unknown, fallback: string) => {
    const { code, message } = refusalOf(err)
    if (code === 'company_changed') setConflict(true)
    else setError(message ?? fallback)
  }

  const save = () => {
    setSaved(false); setError(null); setConflict(false)
    patch.mutate({ id: base.id, patch: changes, expectedUpdatedAt: base.updated_at }, {
      onSuccess: (next) => { setBase(next); setTyped({}); setSaved(true) },
      onError: (err) => refused(err, T.SAVE_FAILED),
    })
  }

  const reload = async () => {
    setReloading(true); setError(null); setSaved(false)
    try {
      const fresh = await readCompany(base.id)
      setConflict(false)
      if (fresh.id !== base.id || fresh.merged_from) {
        // A merged id answers the company it became: say so and offer it, keep this drawer's company.
        setGone({ ...fresh, merged_from: null })
        setMergeKey((k) => k + 1)
        void client.invalidateQueries({ queryKey: companiesKey })
        return
      }
      setBase(fresh); setTyped({})
    } catch {
      setError(T.RELOAD_FAILED)
    } finally {
      setReloading(false)
    }
  }

  const removeSpelling = (spellingId: string) => {
    setSaved(false); setError(null)
    remove.mutate({ id: base.id, spellingId }, {
      // With nothing typed the answer is the new base. With edits typed, keep the stamp the drawer opened
      // on, so a change someone else made meanwhile is still caught on Save.
      onSuccess: (next) => setBase((cur) =>
        Object.keys(typedRef.current).length === 0 ? next : { ...next, updated_at: cur.updated_at }),
      onError: (err) => refused(err, T.REMOVE_FAILED),
    })
  }

  const field = (key: TextKey, id: string, label: string, hint?: string) => (
    <Input id={id} label={label} hint={hint} value={value(key)} disabled={busy}
      onChange={(e) => edit({ [key]: e.target.value })} />
  )

  const usage = T.usageText(base.usage)

  return (
    <Drawer open onOpenChange={(open) => { if (!open) onClose() }}>
      <DrawerContent className="max-w-lg">
        <DrawerHeader>
          <DrawerTitle>{base.name}</DrawerTitle>
        </DrawerHeader>
        <DrawerBody>
          <section className="space-y-3">
            {field('name', ids.name, T.FIELDS.name)}
            {field('short_name', ids.short, T.FIELDS.short)}
            {field('legal_name', ids.legal, T.FIELDS.legal)}
            {field('country', ids.country, T.FIELDS.country, T.COUNTRY_HINT)}
            {field('registration_id', ids.registration, T.FIELDS.registration)}
            <fieldset className="space-y-1.5">
              <legend className="text-sm font-medium text-foreground">{T.FIELDS.roles}</legend>
              <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                {ROLES.map((role) => (
                  <label key={role} className="inline-flex items-center gap-1.5 text-sm">
                    <input type="checkbox" className="accent-primary" checked={roles.includes(role)} disabled={busy}
                      onChange={() => toggleRole(role)} />
                    {roleLabel(role)}
                  </label>
                ))}
              </div>
            </fieldset>
            {base.updated_by_name && <p className="text-xs text-muted-foreground">{T.lastChanged(base.updated_by_name)}</p>}
          </section>

          <section className="space-y-2">
            <h3 className={HEADING}>{T.SPELLINGS_HEADING}</h3>
            {base.spellings.length === 0 ? (
              <p className={QUIET}>{T.SPELLINGS_NONE}</p>
            ) : (
              <ul className="space-y-1.5">
                {base.spellings.map((s) => (
                  <li key={s.id} className="flex items-center justify-between gap-2 text-sm">
                    <span className="min-w-0 [overflow-wrap:anywhere]">
                      {s.text}
                      {s.source === 'merged' && <span className="ml-2 text-xs text-muted-foreground">{T.FROM_MERGE}</span>}
                      {s.source === 'renamed' && <span className="ml-2 text-xs text-muted-foreground">{T.FORMER_NAME}</span>}
                    </span>
                    <Button type="button" variant="ghost" size="sm" aria-label={T.removeLabel(s.text)} disabled={busy}
                      onClick={() => removeSpelling(s.id)}>
                      {T.REMOVE}
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="space-y-2">
            <h3 className={HEADING}>{T.USAGE_HEADING}</h3>
            <p className={QUIET}>{usage || T.NOTHING_NAMES}</p>
          </section>

          <section className="space-y-2">
            <MergeSection key={mergeKey} base={base} names={names} busyElsewhere={busy} onMerged={onMerged}
              onConflict={() => setConflict(true)} />
          </section>
        </DrawerBody>
        <DrawerFooter className="flex-wrap items-center justify-between">
          <div role="status" className="min-h-5 text-sm">
            {gone ? (
              <span className="inline-flex flex-wrap items-center gap-2">
                <span>{T.wasMergedInto(base.name, gone.name)}</span>
                <Button type="button" size="sm" variant="outline" onClick={() => onOpen(gone)}>{T.openCompany(gone.name)}</Button>
              </span>
            ) : conflict ? (
              <span className="inline-flex items-center gap-2">
                <Badge variant="warning-soft">{T.CHANGED}</Badge>
                <Button type="button" size="sm" variant="outline" disabled={reloading} onClick={() => void reload()}>{T.RELOAD}</Button>
              </span>
            ) : error ? (
              <span className="text-destructive">{error}</span>
            ) : saved && patch.isSuccess ? (
              <span className="text-muted-foreground">{T.SAVED}</span>
            ) : null}
          </div>
          <Button type="button" disabled={!dirty || busy || conflict || gone !== null} onClick={save}>
            {patch.isPending ? T.SAVING : T.SAVE}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}
